import { useState, useEffect, useRef, useCallback } from 'react';
import { map, filter, first, concatMap } from 'rxjs/operators';
import { fromEvent, merge } from 'rxjs';
import { swipeDirection, swipeType } from './Models';

export function useWindowSize() {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const minLandscapeWidth = 1900;

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state

      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    function handleOrientationChange() {
      setTimeout(handleResize, 500);
    }

    // Add event listener
    window.addEventListener('resize', handleResize);
    //window.addEventListener('orientationchange', handleOrientationChange);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      //window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []); // Empty array ensures that effect is only run on mount
  return windowSize;
}

export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export function useSwipeEvent(target, handler) {
  let sub = useRef(null);
  let stream = useRef(null);
  const getSwipeStream = useCallback(() => {
    const wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';
    const scrollEvts = fromEvent(target, wheelEvent);
    const toucheStartEvts = fromEvent(target, 'touchstart');
    const toucheEndEvts = fromEvent(target, 'touchend');
    const toucheCancelEvts = fromEvent(target, 'touchcancel');
    let timeStamp = null;
    const waitingPeriod = 1500; // waiting period for prev wheel event to finish
    return merge(
      toucheStartEvts.pipe(
        concatMap(se =>
          merge(toucheEndEvts, toucheCancelEvts).pipe(
            first(),
            map(me => {
              const diff = {
                x: se.touches[0].clientX - me.changedTouches[0].clientX,
                y: se.touches[0].clientY - me.changedTouches[0].clientY,
              };
              if (Math.abs(diff.x) > 20 || Math.abs(diff.y) > 20) {
                if (Math.abs(diff.x) > Math.abs(diff.y)) {
                  return diff.x > 0
                    ? { dir: swipeDirection.RIGHT, origin: swipeType.TOUCH }
                    : { dir: swipeDirection.LEFT, origin: swipeType.TOUCH };
                } else {
                  return diff.y > 0
                    ? { dir: swipeDirection.UP, origin: swipeType.TOUCH }
                    : { dir: swipeDirection.DOWN, origin: swipeType.TOUCH };
                }
              }
            })
          )
        )
      ),
      scrollEvts.pipe(
        map(e => {
          if (e.timeStamp - timeStamp > waitingPeriod) {
            timeStamp = null;
          }
          if ((Math.abs(e.deltaY) > 10 || Math.abs(e.deltaX) > 10) && timeStamp === null) {
            timeStamp = e.timeStamp;
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
              return e.deltaY > 0
                ? { dir: swipeDirection.UP, origin: swipeType.SCROLL }
                : { dir: swipeDirection.DOWN, origin: swipeType.SCROLL };
            } else {
              return e.deltaX > 0
                ? { dir: swipeDirection.RIGHT, origin: swipeType.SCROLL }
                : { dir: swipeDirection.LEFT, origin: swipeType.SCROLL };
            }
          }
          return null;
        }),
        filter(e => e !== null)
      )
    );
  }, [target]);

  useEffect(() => {
    if (!target) {
      return;
    }
    if (!stream.current) {
      stream.current = getSwipeStream();
    }
    sub.current = stream.current.subscribe(handler);
    return () => sub.current.unsubscribe();
  }, [target, handler, getSwipeStream]);
}

export function useScrollEvent(target, handler) {
  let sub = useRef(null);
  let stream = useRef(null);

  const getScrollStream = useCallback(() => {
    const wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';
    const scrollEvts = fromEvent(target, wheelEvent);
    const toucheMoveEvts = fromEvent(target, 'touchmove');
    let prevEvent = null;

    return merge(
      scrollEvts,
      toucheMoveEvts.pipe(
        map(e => {
          let newEvent = null;
          if (prevEvent && e.timeStamp - prevEvent.timeStamp < 100) {

            newEvent = {
              timeStamp: e.timeStamp,
              deltaY: 6 * Math.round(prevEvent.touches[0].clientY - e.touches[0].clientY ),
              deltaX: 6 * Math.round(prevEvent.touches[0].clientX - e.touches[0].clientX ),
            };
          }
          prevEvent = e;

          return newEvent;
        }),
        filter(e => e !== null)
      )
    );
  }, [target]);

  useEffect(() => {
    if (!target) {
      return;
    }
    if (!stream.current) {
      stream.current = getScrollStream();
    }

    sub.current = stream.current.subscribe(handler);
    return () => sub.current.unsubscribe();
  }, [target, handler, getScrollStream]);
}
